<template>
    <Dialog v-if="visible" :size="dialogSize">
        <template v-slot:title>
            無線機設定の編集
        </template>

        <template v-slot:body>
            <div style="margin: 20px;">
                <el-form :label-position="'left'" label-width="100px" :model="form" :rules="rules" ref="form">
                    <FormTextBox
                        v-model="form.radioSettingsName"
                        @input="handleRadioSettingsNameInput"
                        label="無線機設定名"
                        prop="radioSettingsName"
                        :required="true"
                        :disable="false"
                        placeholder="入力してください"
                    />
                </el-form>
                <TwoLevelTabs
                    :modelValueTop="selectedTopTab"
                    @update:modelValueTop="selectedTopTab = $event"
                    :modelValueBottom="selectedBottomTab"
                    @update:modelValueBottom="selectedBottomTab = $event"
                    :topTabs="FIRST_LEVEL_MENU"
                    :bottomTabs="SECOND_LEVEL_MENU"
                />
                <el-scrollbar class="wrapperCurrentTabComponent">
                    <el-form :label-position="'left'">
                        <component
                            :is="currentTabComponent"
                            :key="`${settingId}-${selectedTopTab}-${selectedBottomTab}`"
                            class="currentTabComponent"
                            :enableWifiOnly="canEditWifiItemsOnly"
                            @disableSave="childValid = !$event"
                            @settingChanged="checkForChanges"/>
                    </el-form>
                </el-scrollbar>
            </div>
        </template>

        <template v-slot:button>
            <BaseButton type="default" label="キャンセル" :disable="false" @click="handleCancelClick" />
            <BaseButton type="primary" label="保存する" :disable="disableSave" @click="handleSaveClick" />
        </template>
    </Dialog>
</template>

<script>
import humps from 'humps'
import { componentsMap } from './bcSettingsPanels/BCComponentsMap.js'
import Dialog from '@/components/pc/common/Dialog.vue'
import BaseButton from '@/components/pc/radio/ipradio/atoms/BaseButton.vue'
import FormTextBox from '@/components/pc/radio/ipradio/atoms/FormTextBox.vue'
import TwoLevelTabs from '@/components/pc/radio/ipradio/atoms/TwoLevelTabs.vue'
import { FIRST_LEVEL_MENU } from '@/components/pc/radio/ipradio/config/BCconstants'
import { SECOND_LEVEL_MENU } from '@/components/pc/radio/ipradio/config/BCconstants'
import { useRadioSettingsStore } from '@/components/pc/radio/ipradio/stores/bcRadioSettings'
import server from '@/components/ServerApi.js'
import constants from '@/components/constants.js'
import commonUtil from '@/components/commonutil.js'

export default {

    components: {
        Dialog,
        BaseButton,
        FormTextBox,
        TwoLevelTabs
    },

    props: {
        visible: {
            type: Boolean,
            required: true,
        },

        settingId: {
            type: Number,
            required: true
        },

        settingName: {
            type: String,
            required: true,
        },

        setting: {
            type: Object,
            required: true,
        },
        appData: Object,
    },

    data() {
        return {
            radioSettingsStore: null,

            dialogSize: 'xlarge',

            form: {
                radioSettingsName: '',
            },
            rules: {
                radioSettingsName: [
                    {
                        validator: (rule, value, callback) => {

                            // 空白文字のみチェック（全角・半角スペース）
                            if (/^[\s　]+$/.test(value)) {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // 空文字チェック
                            if (!value || value.trim() === '') {
                                callback(new Error('値を指定してください。'))
                                return
                            }

                            // 文字数制限チェック (50文字以下)
                            if (value.length > 50) {
                                callback(new Error('50文字以下で指定してください。'))
                                return
                            }

                            // 「設定なし」チェック
                            if (value.trim() === '設定なし') {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // バックスラッシュチェック
                            if (value.includes('\\')) {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // 絵文字チェック（Unicode絵文字の範囲をチェック）
                            const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;
                            if (emojiRegex.test(value)) {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // トリム後の値をフォームに設定
                            this.form.radioSettingsName = value.trim()
                            callback()
                        },
                        trigger: 'blur'
                    }
                ]
            },
            selectedTopTab: 'OptionalFeatures', // TODO: 設定メニューから取得
            selectedBottomTab: 'Display', // TODO: 設定メニューから取得

            // 保存ボタンの活性状態を決定する個別のフラグ
            hasChanges: false, // 変更があるか
            formValid: true,  // 親フォームが有効か
            childValid: true,  // 子がOKか

            lockRefreshTimer: null, // ロック更新用タイマー
            originalSettingName: '', // 変更検知のために元の設定名を保持
            originalSetting: null, // 変更検知のために元の設定を保持
            changeCheckInterval: null, // 変更チェック用タイマー
        }
    },

    computed: {
        FIRST_LEVEL_MENU() {
            if (this.canEditWifiItemsOnly) {
                return FIRST_LEVEL_MENU.filter((item) => item.name === 'Network')
            } else {
                return FIRST_LEVEL_MENU;
            }
        },

        SECOND_LEVEL_MENU() {
            if (this.canEditWifiItemsOnly) {
                const targetNames = [
                    'Configuration',
                    'Tone',
                    'Wi-Fi',
                    'Wi-FiManagementList',
                ]

                return {
                    Network: SECOND_LEVEL_MENU.Network.filter((item) => targetNames.includes(item.name))
                }
            } else {
                return SECOND_LEVEL_MENU;
            }
        },

        currentTabComponent() {
            return (
                componentsMap[humps.pascalize(this.selectedTopTab) + humps.pascalize(this.selectedBottomTab)]
                || null
            )
        },

        disableSave() {
            return !(this.hasChanges && this.formValid && this.childValid)
        },
        canManageDefinition() {
            return commonUtil.canManageRadioSettingDefinition(this.appData)
        },
        canEditWifiItemsOnly() {
            return !this.canManageDefinition && commonUtil.canEditRadioSettingWifiItems(this.appData)
        }
    },

    watch: {
        async visible(newVal, oldVal) {
            if (newVal) {
                this.initialize()
                this.acquireLock() // ロックを取得
                this.startLockRefreshTimer() // ロック更新タイマー開始
                this.startChangeCheckInterval()
                window.addEventListener('beforeunload', this.beforeUnloadHandler)
            } else {
                // ダイアログが閉じられたタイミングで片付け
                this.radioSettingsStore.discardTempSetting()
                await this.releaseLock() // ロックを解放（await追加）
                this.stopLockRefreshTimer() // ロック更新タイマー停止
                this.stopChangeCheckInterval()
                window.removeEventListener('beforeunload', this.beforeUnloadHandler)
            }
        },

        // 設定名の変更を監視
        'form.radioSettingsName'(newVal) {
            this.checkForChanges()
        }
    },

    created() {
        this.initialize()
    },

    beforeUnmount() {
        if (this.changeCheckInterval) {
            clearInterval(this.changeCheckInterval)
            this.changeCheckInterval = null
        }
    },

    beforeDestroy() {
        this.stopLockRefreshTimer()
        if (this.changeCheckInterval) {
            clearInterval(this.changeCheckInterval)
            this.changeCheckInterval = null
        }
    },

    methods: {
        closeDialog() {
            this.$emit('update:visible', false)
        },

        handleRadioSettingsNameInput(value) {
            const sanitizedValue = typeof value === 'string' ? value.replace(/[\s　]/g, '') : ''
            if (sanitizedValue !== this.form.radioSettingsName) {
                this.form.radioSettingsName = sanitizedValue
            }

            if (!this.$refs.form) {
                return
            }

            this.$refs.form.validateField('radioSettingsName', (message) => {
                this.formValid = !message
            })
        },

        startChangeCheckInterval() {
            if (this.changeCheckInterval) return
            this.changeCheckInterval = setInterval(() => {
                this.checkForChanges()
            }, 500)
        },

        stopChangeCheckInterval() {
            if (!this.changeCheckInterval) return
            clearInterval(this.changeCheckInterval)
            this.changeCheckInterval = null
        },


        initialize() {
            this.form.radioSettingsName = this.settingName

            if (!this.radioSettingsStore) {
                this.radioSettingsStore = useRadioSettingsStore()
            }

            // まず store を初期化
            this.radioSettingsStore.discardTempSetting?.() // ←あれば呼ぶ
            this.radioSettingsStore.saveSetting(this.setting)

            // ★ props じゃなく store の実値から original を作る
            const base = this.radioSettingsStore.getTempSetting
                ? this.radioSettingsStore.getTempSetting()
                : this.radioSettingsStore.getSetting()

            this.originalSettingName = this.form.radioSettingsName
            this.originalSetting = JSON.parse(JSON.stringify(base))

            this.hasChanges = false
            this.formValid = true
            this.childValid = true

            if (this.canEditWifiItemsOnly) {
                this.selectedTopTab = 'Network'
                this.selectedBottomTab = 'Configuration'
            }

            // ★初期化後に一回チェック（子の初期emitにも対応）
            this.$nextTick(() => this.checkForChanges())
        },

        // 変更があったかどうかをチェックする
        checkForChanges() {
            if (!this.radioSettingsStore) {
                return
            }

            // 設定名の変更チェック
            const nameChanged = this.form.radioSettingsName !== this.originalSettingName

            // 設定内容の変更チェック
            const currentSetting = this.radioSettingsStore.getTempSetting()
            const settingChanged = JSON.stringify(currentSetting) !== JSON.stringify(this.originalSetting)

            // 変更フラグを更新
            this.hasChanges = (nameChanged || settingChanged)

            // フォームのバリデーションチェック
            if (this.$refs.form) {
                this.$refs.form.validate((valid) => {
                    this.formValid = valid
                })
            } else {
                this.formValid = true
            }
        },

        beforeUnloadHandler(event) {
            this.stopLockRefreshTimer()
            this.releaseLock()
        },

        // 保存ボタンハンドラー
        async handleSaveClick() {
            const formRef = this.$refs.form
            formRef.validate(async (valid) => {
                if (!valid) {
                    return
                }

                // ストアに保存されている無線機設定を更新する
                this.radioSettingsStore.saveSetting(this.radioSettingsStore.getTempSetting())
                this.$emit('save', this.form.radioSettingsName, this.radioSettingsStore.getSetting())

                // ロックを明示的に解放してからダイアログを閉じる
                await this.releaseLock()
                this.closeDialog()
            })
        },

        // キャンセルボタンハンドラー
        handleCancelClick() {
            this.closeDialog()
        },

        async acquireLock() {
            try {
                const res = await server.post('/lockipradiosettingedit', { settingId: this.settingId })

                if (res.data.status === constants.STATUS_SUCCESS) {
                    return
                }

                if (res.data.status === constants.STATUS_ERROR_ALREADY_LOCKED) {
                    // 既にロック中
                    commonUtil.notifyMessage(this, 'Warning', '他のユーザーが編集中のため、編集できません。')
                } else {
                    commonUtil.notifyMessage(this, 'Error', commonUtil.getMessageText(this.$i18n, 'error_server_api').replace('{functionName}', 'acquireLock').replace('{code}', res.data.status))
                }
                // 取得に失敗したらダイアログを閉じる
                this.$emit('update:visible', false)
            } catch (e) {
                commonUtil.putExceptionErrorMessage(this, 'acquireLock', e)
                this.$emit('update:visible', false)
            }
        },

        async releaseLock() {
            if (!this.settingId) return
            try {
                await server.post('/unlockipradiosettingedit', { settingId: this.settingId })
            } catch (e) {
                // 解放失敗は致命的でないためログだけ
                commonUtil.putExceptionErrorMessage(this, 'releaseLock', e)
            }
        },

        startLockRefreshTimer() {
            if (this.lockRefreshTimer) return
            this.lockRefreshTimer = setInterval(() => {
                this.refreshLock()
            }, 600000) // 10分
        },

        stopLockRefreshTimer() {
            if (this.lockRefreshTimer) {
                clearInterval(this.lockRefreshTimer)
                this.lockRefreshTimer = null
            }
        },

        async refreshLock() {
            try {
                const res = await server.post('/refreshipradiosettingeditlock', { settingId: this.settingId })
                if (res.data.status !== constants.STATUS_SUCCESS) {
                    commonUtil.notifyMessage(this, 'Warning', commonUtil.getMessageText(this.$i18n, 'error_server_api').replace('{functionName}', 'refreshLock').replace('{code}', res.data.status))
                }
            } catch (e) {
                commonUtil.putExceptionErrorMessage(this, 'refreshLock', e)
            }
        }
    }
}
</script>

<style scoped>
.wrapperCurrentTabComponent {
  height: 64vh;
}
</style>
